import React, { useEffect, useState } from 'react';
import { Table, Form, Alert, Spinner, Container } from 'react-bootstrap';
import { HttpData, HttpNoData } from '../../../Core';
import CreateWorker from '../../Administrator/Workers/CreateWorker';
import EditEmployeeNameModal from './EditEmployeeNameModal';
import {
    getWorkerCategories,
    getWorkerTypes,
    getWorkerMathSalary,
    getWorkerEmploymentTypes,
    getWorkerMathTime,
    getWorkerQualificationTypes,
} from '../../Administrator/Workers/WorkersAPI';

const ROLES = [
    { key: 'administrator', label: 'Администратор' },
    { key: 'manager', label: 'Менеджер' },
    { key: 'analytic', label: 'Аналитик' },
    { key: 'worker', label: 'Работник цеха' },
];

const EmployeeRolesPage = ({ jwt }) => {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [textSearch, setTextSearch] = useState('');
    const [togglingCell, setTogglingCell] = useState(null);
    const [attachWorkerFor, setAttachWorkerFor] = useState(null);
    const [editingNameFor, setEditingNameFor] = useState(null);

    const [workerCategories, setWorkerCategories] = useState([]);
    const [workerTypes, setWorkerTypes] = useState([]);
    const [mathSalaryTypes, setMathSalaryTypes] = useState([]);
    const [employmentTypes, setEmploymentTypes] = useState([]);
    const [mathTimeTypes, setMathTimeTypes] = useState([]);
    const [qualificationTypes, setQualificationTypes] = useState([]);

    const loadData = async () => {
        setLoading(true);
        setError(null);

        try {
            const employeesResponse = await HttpNoData('/api/v1/EmployeeRoles', 'GET', jwt);

            if (!employeesResponse.statusSuccessful)
                throw new Error('Не удалось загрузить список сотрудников');

            setEmployees(employeesResponse.data || []);

            setWorkerCategories((await getWorkerCategories(jwt)) ?? []);
            setWorkerTypes((await getWorkerTypes(jwt)) ?? []);
            setMathSalaryTypes((await getWorkerMathSalary(jwt)) ?? []);
            setEmploymentTypes((await getWorkerEmploymentTypes(jwt)) ?? []);
            setMathTimeTypes((await getWorkerMathTime(jwt)) ?? []);
            setQualificationTypes((await getWorkerQualificationTypes(jwt)) ?? []);
        } catch (e) {
            console.error('Ошибка загрузки:', e);
            setError(e.message || 'Ошибка при загрузке данных');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [jwt]);

    const replaceEmployee = updatedRow => {
        setEmployees(prev => prev.map(e => (e.multiAccountId === updatedRow.multiAccountId ? updatedRow : e)));
    };

    const handleToggle = async (employee, roleKey) => {
        const roleState = employee[roleKey];
        const cellId = `${employee.multiAccountId}:${roleKey}`;

        if (roleState.isActive) {
            setTogglingCell(cellId);
            setError(null);

            const response = await HttpNoData(`/api/v1/EmployeeRoles/${employee.multiAccountId}/${roleKey}`, 'DELETE', jwt);

            if (response.statusSuccessful) replaceEmployee(response.data);
            else setError('Не удалось снять роль');

            setTogglingCell(null);
            return;
        }

        if (roleKey === 'worker' && !roleState.hasEverHad) {
            setAttachWorkerFor(employee);
            return;
        }

        setTogglingCell(cellId);
        setError(null);

        const response = await HttpData(`/api/v1/EmployeeRoles/${employee.multiAccountId}/${roleKey}/restore`, 'PUT', jwt);

        if (response.statusSuccessful) replaceEmployee(response.data);
        else setError('Не удалось выдать роль');

        setTogglingCell(null);
    };

    const handleWorkerCreated = () => {
        if (!attachWorkerFor) return;

        replaceEmployee({
            ...attachWorkerFor,
            worker: { isActive: true, hasEverHad: true, access: 'all' },
        });

        setAttachWorkerFor(null);
    };

    const filteredEmployees = employees.filter(e =>
        (e.firstName + ' ' + e.secondName + ' ' + (e.thirdName ?? ''))
            .toLowerCase()
            .includes(textSearch.toLowerCase())
    );

    return (
        <Container fluid>
            {error && (
                <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Form.Control
                className="mb-3"
                type="text"
                placeholder="Поиск по ФИО"
                value={textSearch}
                onChange={e => setTextSearch(e.target.value)}
            />

            {loading ? (
                <div className="text-center p-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Загрузка данных...</p>
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th style={{ minWidth: '220px' }}>Сотрудник</th>
                                {ROLES.map(role => (
                                    <th key={role.key} className="text-center" style={{ minWidth: '150px' }}>
                                        {role.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEmployees.length === 0 ? (
                                <tr>
                                    <td colSpan={ROLES.length + 1} className="text-center">
                                        Нет сотрудников
                                    </td>
                                </tr>
                            ) : (
                                filteredEmployees.map(employee => (
                                    <tr key={employee.multiAccountId}>
                                        <td
                                            style={{ fontWeight: 'bold', cursor: 'pointer', color: '#0d6efd' }}
                                            onClick={() => setEditingNameFor(employee)}
                                            title="Изменить ФИО"
                                        >
                                            {employee.secondName} {employee.firstName} {employee.thirdName ?? ''}
                                        </td>
                                        {ROLES.map(role => {
                                            const cellId = `${employee.multiAccountId}:${role.key}`;
                                            const roleState = employee[role.key];

                                            return (
                                                <td key={role.key} className="text-center">
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={roleState.isActive}
                                                        disabled={togglingCell === cellId}
                                                        onChange={() => handleToggle(employee, role.key)}
                                                    />
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            )}

            {attachWorkerFor && (
                <CreateWorker
                    show={true}
                    jwt={jwt}
                    onHide={() => setAttachWorkerFor(null)}
                    workerCategories={workerCategories}
                    workerTypes={workerTypes}
                    mathTimeTypes={mathTimeTypes}
                    employmentTypes={employmentTypes}
                    mathSalaryTypes={mathSalaryTypes}
                    qualificationTypes={qualificationTypes}
                    onCreated={handleWorkerCreated}
                    existingMultiAccountId={attachWorkerFor.multiAccountId}
                    existingPersonName={`${attachWorkerFor.secondName} ${attachWorkerFor.firstName}`}
                />
            )}

            {editingNameFor && (
                <EditEmployeeNameModal
                    employee={editingNameFor}
                    jwt={jwt}
                    onHide={() => setEditingNameFor(null)}
                    onUpdated={updatedRow => {
                        replaceEmployee(updatedRow);
                        setEditingNameFor(null);
                    }}
                />
            )}
        </Container>
    );
};

export default EmployeeRolesPage;
